import React, { useState, Fragment, useEffect, useMemo } from 'react';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';

import { DEFAULT_REPORT_TITLE, NEW_UTM_KICKOFF_DATE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { AnomalySummary, ChecklistItem, CustomUTMOverview, CustomUTMPipingSummary, NewAnomalyItem, PDFImageProps, PIDData, UTMInspection, User } from '../../../utils/models';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomExxonMobilUTMPipingReport from './customExxonMobilUTMPipingReport';
import CustomUTMSummaryOverviewForm from '../../forms/custom-utm/customUtmSummaryOverviewForm';
import ChecklistForm from '../custom-visual-inspection/checklist/checklistItem';
import CustomUTMSection from './custom-utm-section';
import CustomAnomalyItems from '../custom-general/custom-anomaly-items';
import Elevation from '../../themed/instruction-modals/elevation';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import FVICoverData from '../../themed/fvi-cover-data';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import MUTPhotograpicRecord from '../../forms/mut/mutPhotographicRecord';
import PipingAnomalySummary from '../../forms/custom-fvi/pipingAnomalySummary';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import VIGeneralDrawingForm from '../../forms/visual-inspection/viGeneralDrawingForm';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import { getCurrentUser } from '../../../services/authService';
import { getPipingAnomalyClassOptions } from '../../../utils/data';
import { getIsSameOrAfter, getSomeChecklistItemsMissing, getUpdatedPipingAnomalySummary, matchPhrases, reArrangeAnomalies, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { PIPING_LOCATION_OPTIONS } from '../custom-visual-inspection/existing-piping-item';

import useAnomalyRecords from '../../../hooks/useAnomalyRecords';
import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useMappedAnomalies from '../../../hooks/useMappedAnomalies';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CreateCustomExxonMobilUTMPipingReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [summary, setSummary] = useState<CustomUTMPipingSummary>(initialPipingSummary);
	const [anomalyItems, setAnomalyItems] = useState<NewAnomalyItem[]>([]);
	const [anomalySummary, setAnomalySummary] = useState<AnomalySummary>(initialAnomalySummary);
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [generalDrawing, setGeneralDrawing] = useState<PDFImageProps[]>([]);
	const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(initialChecklistItems);
	const [checklistAdditionalComment, setChecklistAdditionalComment] = useState('');
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [isoImage, setIsoImage] = useState<PDFImageProps[]>([]);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			reportTitle,
			coverData,
			summary,
			anomalyItems,
			anomalySummary,
			pidData,
			generalDrawing,
			checklistItems,
			checklistAdditionalComment,
			photographicData,
			projectDetails,
			isoImage,
			inspectionOverview: utInspectionOverview,
			utInspectionItems,
		};
	}, [anomalyItems, anomalySummary, projectDetails, checklistAdditionalComment, checklistItems, coverData, isoImage, pidData, generalDrawing, summary, photographicData, reportTitle, utInspectionOverview, utInspectionItems]);

	const mappedAnomalyItems = useMappedAnomalies([{ id: 1, anomalyItems: anomalyItems }]);

	const someChecklistMissing = useMemo(() => {
		return getSomeChecklistItemsMissing(checklistItems, mappedAnomalyItems);
	}, [checklistItems, mappedAnomalyItems]);

	const inspectionItemReadingsMissing = useMemo(() => {
		if (utInspectionItems.length === 0) return true;

		return utInspectionItems.some((item) => !item.sectionTitle || item.items.length === 0);
	}, [utInspectionItems]);

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber))
			return 'Enter a valid work order number!';
		
		if (reportDetails.summary.lineNumber.length === 0) {
			return 'Please complete summary information!';
		}

		if (reportDetails.photographicData.length < 2) {
			return 'Please provide at least 2 photos!';
		}

		if (matchPhrases(reportDetails.pidData.hasPID, 'yes') && reportDetails.pidData.pIDImages.length === 0) {
			return 'P&ID images missing!';
		}

		if (reportDetails.anomalySummary.pitting.length === 0) {
			return 'Please provide a value for pitting!';
		}

		if (reportDetails.anomalySummary.extentOfCoatingBreakdown.length === 0) {
			return 'Please provide a value for Coating Breakdown!';
		}
		
		if (reportDetails.anomalySummary.description.length === 0) {
			return 'Please provide a value for anomaly description!';
		}

		if (reportDetails.anomalySummary.others.length === 0) {
			return 'Please provide a value for anomaly summary (others)!';
		}

		if (matchPhrases(reportDetails.summary.hasAnomalies, 'yes')) {
			if (reportDetails.anomalyItems.length === 0) {
				return 'Please upload at least one anomaly!.'
			}
		}

		if (reportDetails.isoImage.length === 0) {
			return 'Please upload Isometric drawing!';
		}

		if (someChecklistMissing) {
			return someChecklistMissing;
		}

		if (reportDetails.inspectionOverview.equipment.length === 0) {
			return 'Please complete the inspection overview!';
		}
		
		if (inspectionItemReadingsMissing) {
			return 'Please complete the inspection items section!';
		}

		return '';
	}, [inspectionItemReadingsMissing, someChecklistMissing, reportDetails]);

	const isNewUTMTemplate = useMemo(() => {
		if (!projectDetails?.createdAt) return false;

		return getIsSameOrAfter(projectDetails.createdAt, NEW_UTM_KICKOFF_DATE)
	}, [projectDetails?.createdAt]);

	const togglePreviewReport = (): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const dispatch = useAppDispatch();
	const autoSave = useAutoSaveReport(reportDetails);

	useAnomalyRecords(anomalyItems);
	useInspectedItemIDUpdate([summary.lineNumber]);

	useEffect(() => {
		function initializeReport() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			setReportTitle(cachedReport.reportTitle);
			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setAnomalyItems(reArrangeAnomalies(cachedReport.anomalyItems));
			setAnomalySummary(cachedReport.anomalySummary);
			setSummary(cachedReport.summary);
			setPIDData(cachedReport.pidData);
			setGeneralDrawing(cachedReport.generalDrawing ?? []);
			setChecklistItems(cachedReport.checklistItems);
			setChecklistAdditionalComment(cachedReport.checklistAdditionalComment);
			setUTInspectionOverview(cachedReport.inspectionOverview);
			setUTInspectionItems(cachedReport.utInspectionItems);
			setIsoImage(cachedReport.isoImage);

			const photoRecord = reArrangePhotoRecord(cachedReport.photographicData);
			dispatch(setLastPhotoNumber(photoRecord.length));
			setPhotographicData(photoRecord);
		}
		
		initializeReport();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.UTM));
	}, [dispatch]);

	useEffect(() => {
		const newAnomalySummary = getUpdatedPipingAnomalySummary(isNewUTMTemplate, anomalySummary, anomalyItems);
		if (newAnomalySummary) setAnomalySummary(newAnomalySummary);

		// eslint-disable-next-line
	}, [anomalyItems, anomalySummary.others, anomalySummary.description, isNewUTMTemplate]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomExxonMobilUTMPipingReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilUTMPiping[projectDetails.organisationId]}
					isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilUTMPiping && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails?.organisationName} UTM Piping Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title='Incomplete Report' body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
			<Zippy headerTitle="Executive Summary" isValid={summary.lineNumber.length > 0}>
				<CustomUTMSummaryOverviewForm initialSummary={summary} onSave={setSummary} />
			</Zippy>
			
			<Zippy headerTitle='Anomaly Summary & Description' isValid={anomalySummary.pitting.length > 0}>
				<PipingAnomalySummary 
					hasAnomalies={summary.hasAnomalies} 
					initialAnomalySummary={anomalySummary} 
					onAdd={setAnomalySummary} 
					useNewFormat={isNewUTMTemplate}
				/>
			</Zippy>

			<Conditional isVisible={matchPhrases(summary.hasAnomalies, 'yes')}>
				<Zippy headerTitle="Anomaly Items" isValid={anomalyItems.length > 0}>
					<CustomAnomalyItems
						isPDOFormat={false}
						nextAnomalyID={anomalyItems.length + 1}
						locationOptions={PIPING_LOCATION_OPTIONS}
						isNewFormat={isNewUTMTemplate}
						damageClasses={getPipingAnomalyClassOptions()}
						items={anomalyItems}
						onDataChange={(items) => setAnomalyItems(items)}
					/>
				</Zippy>
			</Conditional>

			<Zippy headerTitle="Photographic Report" isValid={photographicData.length > 0}>
				<MUTPhotograpicRecord initialImages={photographicData} onProceed={setPhotographicData} />
			</Zippy>

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Zippy headerTitle="General Drawing (Optional)" isValid={true}>
				<VIGeneralDrawingForm initialGeneralDrawing={generalDrawing} onProceed={setGeneralDrawing} />
			</Zippy>

			<Zippy headerTitle="Checklist Items" isValid={!someChecklistMissing}>
				<ChecklistForm
					enableChecklistItemComment={false}
					label='DETAILS'
					additionalComment={checklistAdditionalComment}
					anomalyItems={mappedAnomalyItems}
					lineNumber={summary.lineNumber}
					items={checklistItems}
					onAddComment={(comment) => setChecklistAdditionalComment(comment)}
					onItemChange={(items) => setChecklistItems(items)}
				/>
			</Zippy>

			<CustomUTMSection
				tmlLocations={isoImage}
				onChangeTMLLocations={setIsoImage}
				utmItems={utInspectionItems}
				utmOverview={utInspectionOverview}
				onItemsChange={setUTInspectionItems}
				onOverviewChange={setUTInspectionOverview}
			/>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialChecklistItems: ChecklistItem[] = [
	{
		id: '1',
		lineNumber: 'Straight pipe runs, including where pipe rest on support',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '2',
		lineNumber: 'Branch connections, including reinforcemnt pads',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '3',
		lineNumber: 'Flanges and associated hardware',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '4',
		lineNumber: "Elbows, reducers and Tee's",
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '5',
		lineNumber: 'Small piping takeoffs (Valves and piping) including Gussets',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '6',
		lineNumber: 'In-line valves (Block, Check, Control Etc)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '7',
		lineNumber: 'Dead Leg (s)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '8',
		lineNumber: 'Temporary repair clamps / Enclosures / Composite wrap',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '9',
		lineNumber: 'Filter, Stainer etc.',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '10',
		lineNumber: 'Insulation / wheather proofing / Jacketing',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '11',
		lineNumber: 'Pipe support - Tee Slides / H" Slides / Base Supports',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '12',
		lineNumber: 'Dummy Legs',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '13',
		lineNumber: 'Anchors / Restrainsts / Guides ',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '14',
		lineNumber: 'Spring Hangers / Support Rods Including Pipe Clamps',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '15',
		lineNumber: 'Thermowels and Temperature Indicating Insturments',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '16',
		lineNumber: 'Orifice Taps ',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '17',
		lineNumber: 'Soil - to - Air Interface (Underground Lines)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '18',
		lineNumber: 'Coating Breakdown',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '19',
		lineNumber: 'Heavy Scale',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '20',
		lineNumber: 'Uncoated Surface',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '21',
		lineNumber: 'Safety valve and Associated Piping ',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '22',
		lineNumber: 'Leak',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '23',
		lineNumber: 'Vibration',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
];

const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
	techniqueType: '',
	velocity: 0,
};

const initialAnomalySummary: AnomalySummary = {
	pipeBody: 'N/A',
	noOfPoints: 0,
	pitting: 'N/A',
	noOfPittingLocations: 0,
	flanges: 'N/A',
	noOfFlanges: 0,
	fasteners: 'N/A',
	valves: 'N/A',
	noOfValves: 0,
	corrosionUnderPipeSupport: 'N/A',
	noOfCups: 0,
	compositeWrapOnLine: 'no',
	noOfWrapPoints: 0,
	conditionOfSmallBorePipings: 'N/A',
	noOfSmallBorePipings: 0,
	others: 'no',
	description: 'N/A',
	extentOfCoatingBreakdown: 'N/A',
};

const initialPipingSummary: CustomUTMPipingSummary= { 
	hasAnomalies: 'no', 
	lineNumber: '', 
	overview: '', 
};

export default CreateCustomExxonMobilUTMPipingReport;
